import { ReactElement, useState } from 'react';
import { Device, DeviceCommand, DeviceConfigWifi } from '@/api';
import {
    UIButton,
    UISection,
    UISectionContent,
    UISectionHeader,
    UIText,
    UITooltip,
    useUIConfirmationModal
} from '@/ui';
import {
    useCancelDeviceWifiAction,
    useUpdateDeviceWifiAction
} from '../actions';
import { useDevicesTranslation } from '../hooks';
import { PendingBadge } from './pending-badge';
import { DiffCell, StatusCell } from './pending-cells';
import { WifiModal } from './wifi-modal';
import styles from './device-wifi-section.module.scss';

export type DeviceWifiSectionProps = {
    device: Device;
    /** Whether the current user may edit this device. */
    canEdit: boolean;
};

/**
 * "Wifi" section of the device page: a description followed by a table of the
 * device's five WiFi slots. Editing WiFi is not a paid feature, so there are no
 * licence guards here.
 */
export function DeviceWifiSection(props: DeviceWifiSectionProps): ReactElement {
    const { device, canEdit } = props;
    const { t } = useDevicesTranslation();

    return (
        <UISection>
            <UISectionHeader title={t('shared.devices.wifi_label')} />
            <UISectionContent>
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 24
                    }}
                >
                    <UIText type="default2">
                        {t('components.device_wifi_section.description')}
                    </UIText>

                    <DeviceWifiList device={device} canEdit={canEdit} />
                </div>
            </UISectionContent>
        </UISection>
    );
}

type DeviceWifiListProps = {
    device: Device;
    canEdit: boolean;
};

// A device has five WiFi slots (indices 0-4). The config record is sparse, so
// we always render all five and treat a missing slot as disabled/empty.
const WIFI_SLOT_COUNT = 5;

const EMPTY = '—';

/**
 * Table of the device's five WiFi slots, each showing its current reported
 * state plus edit/delete buttons. Edit opens the WiFi editor; delete (after
 * confirmation) disables the slot and clears its SSID/password.
 */
function DeviceWifiList(props: DeviceWifiListProps): ReactElement {
    const { device, canEdit } = props;
    const { t } = useDevicesTranslation();
    const { updateDeviceWifi } = useUpdateDeviceWifiAction({
        deviceId: device.id
    });
    const { cancelDeviceWifi } = useCancelDeviceWifiAction({
        deviceId: device.id
    });
    const confirmationModal = useUIConfirmationModal();

    const [editingSlot, setEditingSlot] = useState<number | null>(null);

    function confirmDelete(slot: number) {
        confirmationModal.open({
            title: t('components.device_wifi_section.delete_confirm_title'),
            confirmText: t('components.device_wifi_section.delete_confirm'),
            confirmVariant: 'danger',
            cancelText: t('shared.labels.cancel'),
            onConfirm: () => {
                confirmationModal.setLoading(true);
                updateDeviceWifi({
                    slot,
                    data: { enabled: false, ssid: null, password: null }
                })
                    .catch(() => {}) // Error handled in the action
                    .finally(() => confirmationModal.close());
            },
            onCancel: () => confirmationModal.close()
        });
    }

    function confirmCancel(run: () => Promise<unknown>) {
        confirmationModal.open({
            title: t('shared.devices.cancel_pending_confirm_title'),
            confirmText: t('shared.devices.cancel_pending_confirm'),
            confirmVariant: 'danger',
            cancelText: t('shared.labels.cancel'),
            onConfirm: () => {
                confirmationModal.setLoading(true);
                run()
                    .catch(() => {}) // Error handled in the action
                    .finally(() => confirmationModal.close());
            },
            onCancel: () => confirmationModal.close()
        });
    }

    return (
        <>
            <table className={styles.wifiTable}>
                <thead>
                    <tr>
                        <th>
                            <HeaderText
                                text={t('shared.devices.column_index')}
                            />
                        </th>
                        <th>
                            <HeaderText text={t('shared.labels.status')} />
                        </th>
                        <th>
                            <HeaderText text={t('shared.fields.ssid.label')} />
                        </th>
                        <th>
                            <HeaderText
                                text={t('shared.fields.wifi_password.label')}
                            />
                        </th>
                        <th />
                    </tr>
                </thead>
                <tbody>
                    {Array.from({ length: WIFI_SLOT_COUNT }, (_, index) => (
                        <WifiSlotRow
                            key={index}
                            index={index}
                            slot={device.config?.wifi?.[String(index)]}
                            pendingCommand={device.commands?.find(
                                (c) =>
                                    c.type === 'set_wifi' &&
                                    c.status === 'pending' &&
                                    c.data.wifiIndex === index
                            )}
                            canEdit={canEdit}
                            onEdit={() => setEditingSlot(index)}
                            onDelete={() => confirmDelete(index)}
                            onCancel={() =>
                                confirmCancel(() =>
                                    cancelDeviceWifi({ slot: index })
                                )
                            }
                        />
                    ))}
                </tbody>
            </table>
            <WifiModal
                deviceId={device.id}
                slot={editingSlot ?? 0}
                current={
                    editingSlot !== null
                        ? device.config?.wifi?.[String(editingSlot)]
                        : undefined
                }
                isOpen={editingSlot !== null}
                onClose={() => setEditingSlot(null)}
            />
        </>
    );
}

function HeaderText(props: { text: string }): ReactElement {
    return (
        <UIText variant="small" type="default2">
            {props.text}
        </UIText>
    );
}

/** Display strings for one Wi-Fi slot's columns (current or pending side). */
type WifiCells = {
    enabled: boolean;
    status: string;
    ssid: string;
    /** The raw password (revealable), or null when off / not set. */
    password: string | null;
};

/**
 * One Wi-Fi slot row. When a change is pending, the status and SSID columns show
 * the current value transitioning to the pending one ("old → new"), mirroring the
 * Motion Alerts / geofence design language, and the slot's edit/delete actions
 * are replaced by the clickable "Pending" badge.
 */
function WifiSlotRow(props: {
    index: number;
    slot: DeviceConfigWifi | undefined;
    pendingCommand: DeviceCommand | undefined;
    canEdit: boolean;
    onEdit: () => void;
    onDelete: () => void;
    onCancel: () => void;
}): ReactElement {
    const { index, slot, pendingCommand, canEdit, onEdit, onDelete, onCancel } =
        props;
    const { t } = useDevicesTranslation();

    const [showPassword, setShowPassword] = useState(false);

    const current = currentCells(slot, t);
    const pending = pendingCommand
        ? pendingCells(pendingCommand.data, t)
        : undefined;
    const enabled = current.enabled;

    // The on/off arrow lives on the Status column. The SSID column only diffs
    // when both sides are enabled; on an enable/disable change it shows a single
    // side. The password can't be diffed (masked), so we just show the resulting
    // side's value (the pending one when the slot stays/becomes enabled).
    const bothEnabled = !!pending && current.enabled && pending.enabled;
    const details =
        pending && !bothEnabled && pending.enabled ? pending : current;
    const passwordSide = pending && pending.enabled ? pending : current;

    return (
        <tr>
            <td>
                <UIText>{index + 1}</UIText>
            </td>
            <td>
                <StatusCell current={current} pending={pending} />
            </td>
            <td>
                <DiffCell
                    current={details.ssid}
                    pending={bothEnabled ? pending?.ssid : undefined}
                />
            </td>
            <td>
                {passwordSide.password ? (
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 4
                        }}
                    >
                        <UIText type="default2">
                            {showPassword ? passwordSide.password : '••••••••'}
                        </UIText>
                        <UITooltip
                            text={
                                showPassword
                                    ? t(
                                          'components.device_wifi_section.hide_password'
                                      )
                                    : t(
                                          'components.device_wifi_section.show_password'
                                      )
                            }
                        >
                            <UIButton
                                leftIcon={showPassword ? 'eye-off' : 'eye'}
                                variant="textDefault"
                                type="button"
                                customPadding={6}
                                onClick={() => setShowPassword((prev) => !prev)}
                            />
                        </UITooltip>
                    </div>
                ) : (
                    <UIText type="default2">{EMPTY}</UIText>
                )}
            </td>
            <td>
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        gap: 4
                    }}
                >
                    {pendingCommand ? (
                        <PendingBadge
                            label={t('shared.labels.pending')}
                            tooltip={
                                canEdit
                                    ? t('shared.devices.cancel_pending_confirm')
                                    : undefined
                            }
                            onCancel={canEdit ? onCancel : undefined}
                        />
                    ) : (
                        canEdit && (
                            <>
                                <UITooltip text={t('shared.labels.edit')}>
                                    <UIButton
                                        leftIcon="edit-2"
                                        variant="textDefault"
                                        type="button"
                                        customPadding={6}
                                        onClick={onEdit}
                                    />
                                </UITooltip>
                                {enabled && (
                                    <UITooltip text={t('shared.labels.delete')}>
                                        <UIButton
                                            leftIcon="trash-2"
                                            variant="textDanger"
                                            type="button"
                                            customPadding={6}
                                            onClick={onDelete}
                                        />
                                    </UITooltip>
                                )}
                            </>
                        )
                    )}
                </div>
            </td>
        </tr>
    );
}

/** Column display strings for the slot's current reported state. */
function currentCells(
    slot: DeviceConfigWifi | undefined,
    t: ReturnType<typeof useDevicesTranslation>['t']
): WifiCells {
    const enabled = slot?.enabled ?? false;
    return {
        enabled,
        status: enabled
            ? t('shared.labels.enabled')
            : t('shared.labels.disabled'),
        ssid: enabled && slot?.ssid ? slot.ssid : EMPTY,
        password: enabled && slot?.password ? slot.password : null
    };
}

/** Column display strings for a pending set_wifi command's target state. */
function pendingCells(
    data: DeviceCommand['data'],
    t: ReturnType<typeof useDevicesTranslation>['t']
): WifiCells {
    const enabled = !!data.wifiEnabled;
    return {
        enabled,
        status: enabled
            ? t('shared.labels.enabled')
            : t('shared.labels.disabled'),
        ssid: enabled && data.wifiSsid ? data.wifiSsid : EMPTY,
        password: enabled && data.wifiPassword ? data.wifiPassword : null
    };
}
